import { Pagination } from '@mui/material';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { CiEdit } from "react-icons/ci";
import { MdDeleteOutline } from 'react-icons/md';
import { FaChevronDown, FaChevronUp, FaImage } from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { BACKEND_SERVER_URL } from '../../../http/http-config';
import '../common-css/Table.css';
import { deleteItem, showTableResponse } from '../common-helpers/commonServices';
import { sortNeetTopics, sortNeetList } from './neetSortHelper';
import NEET_SectionModal from './Neet_SectionModal';
import NEET_TopicModal from './NEET_TopicModal';
import NEET_SettingModal from './NEET_SettingModal';
import NEET_SubjectModal from './NEET_SubjectModal';

const NEET_AllSubjects = () => {
    const sectionRef = useRef();
    const topicRef = useRef();
    const settingRef = useRef();
    const subjectRef = useRef();
    const fileInputRef = useRef(null);

    const [subjects, setSubjects] = useState([]);
    const [sections, setSections] = useState([]);
    const [topics, setTopics] = useState([]);
    const [settings, setSettings] = useState([]);

    const [selectedSections, setSelectedSections] = useState([]);
    const [selectedTopicsDropdown, setSelectedTopicsDropdown] = useState([]);

    const [selectedSubject, setSelectedSubject] = useState(() => {
        const saved = sessionStorage.getItem('neet_selectedSubject');
        return saved ? JSON.parse(saved) : { Subject: "NEET All Subjects" };
    });
    const [selectedSection, setSelectedSection] = useState(() => {
        const saved = sessionStorage.getItem('neet_selectedSection');
        return saved ? JSON.parse(saved) : { Section: "All SECTIONS" };
    });
    const [selectedTopic, setSelectedTopic] = useState(() => {
        const saved = sessionStorage.getItem('neet_selectedTopic');
        return saved ? JSON.parse(saved) : { Topic: "All TOPICS" };
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [checked, setChecked] = useState([]);
    const [loadData, setLoadData] = useState(false);
    const [showImagePanel, setShowImagePanel] = useState(false);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [topicSortDirection, setTopicSortDirection] = useState('asc'); // 'asc' | 'desc'

    const handleTopicSortToggle = () => {
        const nextDirection = topicSortDirection === 'asc' ? 'desc' : 'asc';
        setTopicSortDirection(nextDirection);
        setChecked(prev => sortNeetTopics(prev, nextDirection));
        setTopics(prev => sortNeetTopics(prev, nextDirection));
        setSelectedTopicsDropdown(prev => sortNeetTopics(prev, nextDirection));
    };

    // Fetch all data
    useEffect(() => {
        const response = axios.all([
            axios.get(BACKEND_SERVER_URL + '/neet/subjects'),
            axios.get(BACKEND_SERVER_URL + '/neet/sections'),
            axios.get(BACKEND_SERVER_URL + '/neet/topics'),
            axios.get(BACKEND_SERVER_URL + '/neet/settings')
        ]);

        response.then(axios.spread((subjectsResponse, sectionsResponse, topicsResponse, settingsResponse) => {
            let fetchedSubjects = sortNeetList(subjectsResponse.data.subjects || [], 'Subject');
            let fetchedSections = sortNeetList(sectionsResponse.data.sections || [], 'Section');
            let fetchedTopics = topicsResponse.data.topics || [];
            let fetchedSettings = sortNeetList(settingsResponse.data.data || [], 'Subject');

            setSubjects(fetchedSubjects);
            setSections(fetchedSections);
            setTopics(sortNeetTopics(fetchedTopics, topicSortDirection));
            setSettings(fetchedSettings);

            // Re-apply filters from storage
            const savedSubject = sessionStorage.getItem('neet_selectedSubject') ? JSON.parse(sessionStorage.getItem('neet_selectedSubject')) : { Subject: "NEET All Subjects" };
            const savedSection = sessionStorage.getItem('neet_selectedSection') ? JSON.parse(sessionStorage.getItem('neet_selectedSection')) : { Section: "All SECTIONS" };
            const savedTopic = sessionStorage.getItem('neet_selectedTopic') ? JSON.parse(sessionStorage.getItem('neet_selectedTopic')) : { Topic: "All TOPICS" };

            let finalChecked = [...sortNeetTopics(fetchedTopics, topicSortDirection)];

            if (savedSubject && savedSubject.Subject !== 'NEET All Subjects') {
                let filteredSections = fetchedSections.filter(sec => sec.SubjectID === savedSubject.Sn);
                setSelectedSections(sortNeetList(filteredSections, 'Section'));
                finalChecked = finalChecked.filter(topic => topic.SubjectID === savedSubject.Sn);
            } else {
                setSelectedSections([]);
            }

            if (savedSection && savedSection.Section !== 'All SECTIONS') {
                let filteredTopicsList = fetchedTopics.filter(topic => topic.SectionID === savedSection.Sn);
                setSelectedTopicsDropdown(sortNeetTopics(filteredTopicsList, topicSortDirection));
                finalChecked = finalChecked.filter(topic => topic.SectionID === savedSection.Sn);
            } else {
                setSelectedTopicsDropdown([]);
            }

            if (savedTopic && savedTopic.Topic !== 'All TOPICS') {
                finalChecked = finalChecked.filter(topic => topic.Sn === savedTopic.Sn);
            }

            setChecked(finalChecked);
        })).catch((error) => {
            console.log(error);
            toast.error(error.response?.data?.message || "An error occurred while fetching NEET data.");
        });

    }, [loadData])

    const pageSize = 100;
    const lastIndex = currentPage * pageSize;
    const firstIndex = lastIndex - pageSize;
    const records = checked?.slice(firstIndex, lastIndex);
    const npage = Math.ceil(checked?.length / pageSize)
    const number = [...Array(npage + 1).keys()].slice(1)

    const selectSubjectHandler = (subject) => {
        setSelectedSection({ Section: "All SECTIONS" });
        sessionStorage.setItem('neet_selectedSection', JSON.stringify({ Section: "All SECTIONS" }));
        setSelectedTopic({ Topic: "All TOPICS" });
        sessionStorage.setItem('neet_selectedTopic', JSON.stringify({ Topic: "All TOPICS" }));

        if (!subject || subject.Subject === 'NEET All Subjects') {
            setChecked([...sortNeetTopics(topics, topicSortDirection)]);
            setSelectedSubject({ Subject: "NEET All Subjects" });
            sessionStorage.setItem('neet_selectedSubject', JSON.stringify({ Subject: "NEET All Subjects" }));
            setSelectedSections([]);
            setSelectedTopicsDropdown([]);
            return;
        }

        setSelectedSubject(subject);
        sessionStorage.setItem('neet_selectedSubject', JSON.stringify(subject));

        let filteredSections = sections.filter(sec => sec.SubjectID === subject.Sn);
        setSelectedSections(sortNeetList(filteredSections, 'Section'));

        let filteredTopics = topics.filter(topic => topic.SubjectID === subject.Sn);
        setChecked([...sortNeetTopics(filteredTopics, topicSortDirection)]);
        setSelectedTopicsDropdown([]);
    }

    const selectSectionHandler = (section) => {
        setSelectedTopic({ Topic: "All TOPICS" });
        sessionStorage.setItem('neet_selectedTopic', JSON.stringify({ Topic: "All TOPICS" }));

        if (!section || section.Section === 'All SECTIONS') {
            let filteredTopics = topics.filter(topic => topic.SubjectID === selectedSubject.Sn);
            setChecked(filteredTopics.length > 0 ? [...sortNeetTopics(filteredTopics, topicSortDirection)] : [...sortNeetTopics(topics, topicSortDirection)]);
            setSelectedSection({ Section: "All SECTIONS" });
            sessionStorage.setItem('neet_selectedSection', JSON.stringify({ Section: "All SECTIONS" }));
            setSelectedTopicsDropdown([]);
            return;
        }

        setSelectedSection(section);
        sessionStorage.setItem('neet_selectedSection', JSON.stringify(section));
        let filteredTopics = topics.filter(topic => topic.SectionID === section.Sn);
        setSelectedTopicsDropdown(sortNeetTopics(filteredTopics, topicSortDirection));
        setChecked([...sortNeetTopics(filteredTopics, topicSortDirection)]);
    }

    const selectTopicHandler = (topic) => {
        if (!topic || topic.Topic === 'All TOPICS') {
            let filteredTopics = topics.filter(t => t.SectionID === selectedSection.Sn);
            setChecked(filteredTopics.length > 0 ? [...sortNeetTopics(filteredTopics, topicSortDirection)] : [...sortNeetTopics(topics, topicSortDirection)]);
            setSelectedTopic({ Topic: "All TOPICS" });
            sessionStorage.setItem('neet_selectedTopic', JSON.stringify({ Topic: "All TOPICS" }));
            return;
        }

        setSelectedTopic(topic);
        sessionStorage.setItem('neet_selectedTopic', JSON.stringify(topic));
        setChecked([topic]);
    }

    // Image upload handler for a subject setting
    const handleImageUpload = async (settingItem, file) => {
        if (!file) return;
        setIsFileUploading(true);
        try {
            const formData = new FormData();
            formData.append('uploadedFile', file, file.name);
            const uploadRes = await axios.post(BACKEND_SERVER_URL + '/uploadFile', formData);
            if (uploadRes.data.statusCode === 201) {
                // Update the setting with the new thumbnail
                await axios.post(BACKEND_SERVER_URL + '/neet/update-settings', {
                    ...settingItem,
                    Thumbnail: uploadRes.data.imagePath
                });
                toast.success('Thumbnail uploaded successfully');
                setLoadData(!loadData);
            }
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.message || 'Failed to upload thumbnail');
        } finally {
            setIsFileUploading(false);
        }
    };

    // Helper functions to find entities for editing
    const findSection = (sectionID) => sections.find(s => s.Sn === sectionID);
    const findTopic = (topicID) => topics.find(t => t.Sn === topicID);

    return (
        <>
            <div className="row">
                <div className="col-xl-12">
                    <div className="row">
                        {/* Image Upload Panel */}
                        <div className="col-xl-12 mb-2">
                            <div className="card mb-0" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                                <div
                                    className="card-header d-flex justify-content-between align-items-center py-2 px-3"
                                    style={{
                                        backgroundColor: '#274065',
                                        color: 'white',
                                        cursor: 'pointer',
                                        minHeight: '70px',
                                        borderRadius: '8px 8px 0 0',
                                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                                    }}
                                    onClick={() => setShowImagePanel(!showImagePanel)}
                                >
                                    <div className="d-flex align-items-center gap-3">

                                        <div>
                                            <h6 className="mb-0 text-white" style={{ fontSize: '15px', fontWeight: '700', letterSpacing: '0.3px' }}>
                                                📋 Test Settings — Manage Thumbnails, Duration, Marks & Questions
                                            </h6>
                                            <span className="text-white" style={{ fontSize: '12px', opacity: 0.9 }}>
                                                Click to expand — Upload subject images, set question count, marks per question, negative marking & more
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ color: 'white', opacity: 0.8 }}>
                                        {showImagePanel ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}
                                    </div>
                                </div>
                                {showImagePanel && (
                                    <div className="card-body p-3">
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-sm mb-1">
                                                <thead style={{ backgroundColor: '#f5f5f5' }}>
                                                    <tr>
                                                        <th style={{ fontSize: '12px', fontWeight: '600', width: '40px' }}>Sl.No.</th>
                                                        <th style={{ fontSize: '12px', fontWeight: '600', width: '150px' }}>Subject</th>
                                                        <th style={{ fontSize: '12px', fontWeight: '600', width: '120px' }}>Current Thumbnail</th>
                                                        <th style={{ fontSize: '12px', fontWeight: '600' }}>Upload New Image</th>
                                                        <th style={{ fontSize: '12px', fontWeight: '600', width: '100px' }}>Manage</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {settings.length > 0 ? settings.map((setting, idx) => (
                                                        <tr key={setting.Sn || idx}>
                                                            <td className="text-center align-middle" style={{ fontSize: '12px' }}>{idx + 1}</td>
                                                            <td className="align-middle" style={{ fontSize: '13px', fontWeight: 'bold' }}>{setting.Subject}</td>
                                                            <td className="text-center align-middle">
                                                                {setting.Thumbnail ? (
                                                                    <img
                                                                        src={setting.Thumbnail}
                                                                        alt={setting.Subject}
                                                                        style={{ width: '80px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                                                                    />
                                                                ) : (
                                                                    <span className="badge badge-secondary" style={{ fontSize: '10px' }}>No Image</span>
                                                                )}
                                                            </td>
                                                            <td className="align-middle">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <input
                                                                        type="file"
                                                                        accept="image/jpeg, image/png, image/gif"
                                                                        className="form-control form-control-sm"
                                                                        style={{ fontSize: '12px', maxWidth: '300px' }}
                                                                        onChange={(e) => {
                                                                            if (e.target.files[0]) {
                                                                                handleImageUpload(setting, e.target.files[0]);
                                                                            }
                                                                        }}
                                                                    />
                                                                    {isFileUploading && <Spinner size="sm" animation="border" variant="info" />}
                                                                </div>
                                                            </td>
                                                            <td className="text-center align-middle">
                                                                <button
                                                                    className="btn btn-info btn-xs"
                                                                    onClick={() => settingRef.current.openModal(setting)}
                                                                >
                                                                    Manage
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center p-3" style={{ fontSize: '13px', color: '#888' }}>
                                                                No subject settings found. Please add subjects first via Test Settings.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-xl-12">
                            <div className=" page-title p-2 mb-2">
                                <div className="row w-100 d-flex justify-content-between">
                                    <div className='col-12 d-flex flex-wrap justify-content-start align-items-center gap-2'>
                                        <div className="col-12 col-md-auto d-flex flex-wrap gap-2 justify-content-start">
                                            <button type="button" className="btn btn-dark btn-xs" style={{ whiteSpace: 'nowrap', backgroundColor: '#274065', borderColor: '#274065' }}
                                                onClick={() => subjectRef.current.openModal()}>+ SUBJECT</button>
                                            <button type="button" className="btn btn-info btn-xs text-white" style={{ whiteSpace: 'nowrap', backgroundColor: '#00a99d', borderColor: '#00a99d' }}
                                                onClick={() => sectionRef.current.openModal({
                                                    Subject: selectedSubject.Subject !== 'NEET All Subjects' ? selectedSubject.Subject : '',
                                                    SubjectID: selectedSubject.Sn > 0 ? selectedSubject.Sn : 0
                                                })}>+ SECTION</button>
                                            <button type="button" className="btn btn-primary btn-xs" style={{ whiteSpace: 'nowrap', backgroundColor: '#4d44b5', borderColor: '#4d44b5' }}
                                                onClick={() => topicRef.current.openModal({
                                                    Subject: selectedSubject.Subject !== 'NEET All Subjects' ? selectedSubject.Subject : '',
                                                    SubjectID: selectedSubject.Sn > 0 ? selectedSubject.Sn : 0,
                                                    Section: selectedSection.Section !== 'All SECTIONS' ? selectedSection.Section : '',
                                                    SectionID: selectedSection.Sn > 0 ? selectedSection.Sn : 0
                                                })}>+ TOPIC</button>
                                        </div>

                                        <div className="col-12 col-md-auto d-flex flex-wrap justify-content-start align-items-center gap-2">
                                            <div style={{ minWidth: '170px' }}>
                                                <Select
                                                    value={{ value: selectedSubject, label: selectedSubject.Subject }}
                                                    onChange={(selectedOption) => selectSubjectHandler(selectedOption.value)}
                                                    options={[{ Sn: 0, Subject: 'NEET All Subjects' }, ...subjects]?.map((subject) => ({ value: subject, label: subject.Subject }))}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-5 col-md-2" style={{ minWidth: '160px' }}>
                                                <Select
                                                    isDisabled={selectedSubject.Subject === 'NEET All Subjects'}
                                                    value={{ value: selectedSection, label: selectedSection.Section }}
                                                    onChange={(selectedOption) => selectSectionHandler(selectedOption.value)}
                                                    options={[{ Sn: 0, Section: 'All SECTIONS' }, ...selectedSections]?.map((section) => ({ value: section, label: section.Section }))}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-5 col-md-2" style={{ minWidth: '160px' }}>
                                                <Select
                                                    isDisabled={selectedSection.Section === 'All SECTIONS'}
                                                    value={{ value: selectedTopic, label: selectedTopic.Topic }}
                                                    onChange={(selectedOption) => selectTopicHandler(selectedOption.value)}
                                                    options={[{ Sn: 0, Topic: 'All TOPICS' }, ...selectedTopicsDropdown]?.map((topic) => ({ value: topic, label: topic.Topic }))}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-12 wow fadeInUp" data-wow-delay="1.5s">
                            <div className="d-sm-flex text-center justify-content-between align-items-center">
                                <div className='dataTables_info text-dark'>
                                    Records: {checked.length}
                                </div>
                                {checked.length > pageSize && (
                                    <div className="dataTables_paginate paging_simple_numbers justify-content-center mb-0 pt-2">
                                        <Pagination count={number.length} variant="outlined" color='primary' size="small" page={currentPage} onChange={(e, p) => setCurrentPage(parseInt(p))} />
                                    </div>
                                )}
                            </div>

                            <div className="table-responsive full-data" style={{ paddingBottom: "1px", transform: 'rotateX(180deg)' }}>
                                <div id="example-student_wrapper" className="dataTables_wrapper no-footer">
                                    <table className="table-responsive-lg table display dataTablesCard student-tab dataTable no-footer mt-0 mb-0" style={{ transform: 'rotateX(180deg)' }}>
                                        <thead style={{ backgroundColor: "#cccccc" }} >
                                            <tr className='text-center border border-left'>
                                                <th className='custom-table-header p-2 text-start'>Sl.No.</th>
                                                <th className='custom-table-header p-2 text-center'>SUBJECT</th>
                                                <th className='custom-table-header p-2 text-center'>SECTION</th>
                                                <th 
                                                    className='custom-table-header p-2 text-center'
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={handleTopicSortToggle}
                                                    title="Click to toggle Topic Natural Sorting (Ascending / Descending)"
                                                >
                                                    TOPIC <span style={{ fontSize: '11px', marginLeft: '4px' }}>{topicSortDirection === 'asc' ? '▲' : '▼'}</span>
                                                </th>
                                                <th className='custom-table-header p-2 text-center'>COUNT</th>
                                                <th className='custom-table-header p-2 text-center'>ACTION</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {records.length > 0 ? records.map((item, ind) => (
                                                <tr key={ind} className={`${ind % 2 == 1 ? 'custom row-height' : 'row-height'}`} >
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "50px" }}>
                                                        <h6 className="mb-0 " style={{ fontSize: '12px' }}>
                                                            {firstIndex + ind + 1}
                                                        </h6>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc", width: "150px" }}>
                                                        <h6 className="mb-0 " style={{ fontSize: '13px', fontWeight: 'bold' }}>
                                                            {item.Subject}
                                                        </h6>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc", width: "180px" }}>
                                                        <div className="d-flex justify-content-between align-items-center px-2">
                                                            <h6 className="mb-0" style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.Section}
                                                                <CiEdit cursor='pointer' title='Edit SECTION' size={18} className='text-info ms-3'
                                                                    onClick={() => sectionRef.current.openModal(findSection(item.SectionID))} />
                                                                <MdDeleteOutline color="red" title='Delete SECTION' cursor='pointer' size={18} className='ms-2'
                                                                    onClick={() => deleteItem(item.SectionID, 'SECTION', '/neet/deleteSection/', setLoadData, loadData)} />
                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc" }}>
                                                        <div className="d-flex justify-content-between align-items-center px-2">
                                                            <h6 className="mb-0" style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.Topic}
                                                                <CiEdit cursor='pointer' title='Edit TOPIC' size={18} className='text-info ms-3'
                                                                    onClick={() => topicRef.current.openModal(item)} />
                                                                <MdDeleteOutline color="red" title='Delete TOPIC' cursor='pointer' size={18} className='ms-2'
                                                                    onClick={() => deleteItem(item.Sn, 'TOPIC', '/neet/deleteTopic/', setLoadData, loadData)} />
                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc", width: "180px" }}>
                                                        <div className="d-flex justify-content-center align-items-center gap-2 p-1">
                                                            <div className="d-flex align-items-center gap-1">
                                                                <span style={{ fontSize: '10px', fontWeight: '600' }}>Assigned:</span>
                                                                <span className="badge badge-success py-1 px-1" style={{ fontSize: '10px', minWidth: '25px' }}>{item.NoOfQuestion ?? 0}</span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <span style={{ fontSize: '10px', fontWeight: '600' }}>Available:</span>
                                                                <span className="badge badge-info py-1 px-1" style={{ fontSize: '10px', minWidth: '25px' }}>{item.AvailableQuestions ?? 0}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "100px" }}>
                                                        <button type="button" className="btn btn-primary btn-xxs shadow"
                                                            onClick={() => {
                                                                // Navigate to question bank with pre-selected filters
                                                                window.location.href = `/neet-questions?subjectId=${item.SubjectID}&sectionId=${item.SectionID}&topicId=${item.Sn}`;
                                                            }}
                                                        >Add Question</button>
                                                    </td>
                                                </tr>
                                            )) :
                                                <tr>
                                                    <td colSpan="6" className="text-center p-5">
                                                        {showTableResponse(topics, checked)}
                                                    </td>
                                                </tr>}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <NEET_SectionModal ref={sectionRef} loadSubjects={loadData} setSubjectLoading={setLoadData} />
            <NEET_TopicModal ref={topicRef} loadSubjects={loadData} setSubjectLoading={setLoadData} />
            <NEET_SettingModal ref={settingRef} loadData={loadData} setDataLoading={setLoadData} />
            <NEET_SubjectModal ref={subjectRef} loadSubjects={loadData} setSubjectLoading={setLoadData} />
        </>
    );
};

export default NEET_AllSubjects;
